import { useEffect, useState } from "react";
import { AlertTriangle, Building2, DollarSign, Loader2, MessageSquareText, Star } from "lucide-react";
import { toast } from "sonner";
import { AppHeader } from "../components/AppHeader";
import { Kpi } from "../components/StatCards";
import { FeedbackFeed } from "../components/FeedbackFeed";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { api, errMsg, fmtAvg, onTrial } from "../lib/api";

const STATUS = {
  active: "bg-[#E3EEF3] text-[#2E6F8E]",
  pending: "bg-amber-50 text-amber-700",
  canceled: "bg-slate-100 text-slate-600",
  trial: "bg-[#E3EDE6] text-[#244A3B]",
};

const BusinessTable = ({ businesses }) => (
  <div className="rounded-2xl border border-slate-200/80 bg-white overflow-x-auto" data-testid="superadmin-business-list">
    <table className="w-full text-sm min-w-[720px]">
      <thead className="bg-slate-50 text-left">
        <tr className="label-caps">
          {["Location", "Owner", "Status", "Responses", "Avg", "Flagged", "Survey"].map((h) => <th key={h} className="px-5 py-3 font-semibold">{h}</th>)}
        </tr>
      </thead>
      <tbody className="divide-y divide-slate-100">
        {businesses.map((b) => (
          <tr key={b.id} className="hover:bg-slate-50/70 transition-colors" data-testid={`admin-business-row-${b.slug}`}>
            <td className="px-5 py-4"><p className="font-semibold text-[#1A2B25]">{b.name}</p><p className="text-xs text-[#778580]">{[b.location, b.category].filter(Boolean).join(" · ") || "—"}</p></td>
            <td className="px-5 py-4 text-[#4B5A54]">{b.owner_email}</td>
            <td className="px-5 py-4"><span className={`rounded-full px-2.5 py-1 text-xs font-bold uppercase ${STATUS[onTrial(b) ? "trial" : b.subscription_status]}`}>{onTrial(b) ? "trial" : b.subscription_status}</span></td>
            <td className="px-5 py-4 font-semibold">{b.stats.total}</td>
            <td className="px-5 py-4 font-semibold">{fmtAvg(b.stats.avg_overall)}</td>
            <td className={`px-5 py-4 font-semibold ${b.stats.flagged ? "text-red-600" : ""}`}>{b.stats.flagged}</td>
            <td className="px-5 py-4"><a href={`/s/${b.slug}`} target="_blank" rel="noreferrer" className="text-[#2F5D4A] font-semibold hover:underline">/s/{b.slug}</a></td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

export default function Admin() {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get("/admin/overview").then((r) => setData(r.data)).catch((e) => toast.error(errMsg(e)));
  }, []);

  return (
    <div className="min-h-screen bg-[#EEF1ED]">
      <AppHeader />
      <main className="max-w-7xl mx-auto px-4 sm:px-8 py-8 sm:py-12">
        <p className="label-caps">Super admin</p>
        <h1 className="mt-1 font-display text-3xl sm:text-4xl font-extrabold tracking-tight mb-8" data-testid="admin-title">Platform overview</h1>
        {!data ? (
          <div className="py-20 grid place-items-center"><Loader2 className="w-7 h-7 animate-spin text-[#2F5D4A]" /></div>
        ) : (
          <>
            <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 sm:gap-5 mb-10">
              <Kpi label="Locations" value={data.totals.businesses} icon={Building2} testId="admin-total-businesses" sub={`${data.totals.active} active`} />
              <Kpi label="MRR" value={`$${Number(data.totals.mrr).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`} icon={DollarSign} testId="admin-mrr" sub={`$29.99 × paying locations${data.totals.trialing ? ` · ${data.totals.trialing} on trial` : ""}`} />
              <Kpi label="Responses" value={data.stats.total} icon={MessageSquareText} testId="admin-total-responses" />
              <Kpi label="Avg rating" value={fmtAvg(data.stats.avg_overall)} icon={Star} testId="dashboard-avg-rating-value" sub="Across all businesses" />
              <Kpi label="Needs attention" value={data.stats.flagged} icon={AlertTriangle} tone={data.stats.flagged ? "alert" : "default"} testId="admin-flagged-count" />
            </div>
            <Tabs defaultValue="feedback">
              <TabsList className="rounded-full bg-slate-100 p-1 mb-5">
                <TabsTrigger value="feedback" className="rounded-full px-4" data-testid="admin-tab-feedback">All feedback</TabsTrigger>
                <TabsTrigger value="businesses" className="rounded-full px-4" data-testid="admin-tab-businesses">Locations</TabsTrigger>
              </TabsList>
              <TabsContent value="feedback">
                <FeedbackFeed items={data.feedback} showBusiness businesses={data.businesses} testId="superadmin-all-feedback-table" />
              </TabsContent>
              <TabsContent value="businesses"><BusinessTable businesses={data.businesses} /></TabsContent>
            </Tabs>
          </>
        )}
      </main>
    </div>
  );
}
